/**
 * Axios Configuration with Global 401 Interceptor
 *
 * This module sets up axios interceptors to handle authentication errors globally.
 * When a 401 response is received from the backend (indicating the user's token has been
 * blacklisted or revoked), all devices/windows are logged out immediately.
 */

import axios from 'axios';
import { toast } from 'react-toastify';

// TIER 2: Track the current response interceptor ID to prevent stacking
let currentResponseInterceptorId = null;

/**
 * Configure axios response interceptor for global 401 handling
 * This ensures that if ANY API call returns 401, the user is logged out immediately
 *
 * CRITICAL FIX: Remove the previous interceptor before registering a new one
 * This prevents stacking of interceptors which causes unpredictable behavior
 *
 * @param {Function} handleLogout - Callback function to handle logout (legacy support)
 * @param {Function} performLogout - Optional callback for logout with reason parameter
 */
export const setupAxiosInterceptors = (handleLogout, performLogout) => {
  // Remove previous interceptor if it exists (prevent stacking)
  if (currentResponseInterceptorId !== null) {
    axios.interceptors.response.eject(currentResponseInterceptorId);
  }

  // Register interceptor and store its ID
  currentResponseInterceptorId = axios.interceptors.response.use(
    // Success response - pass through
    (response) => {
      // Log successful API calls for debugging
      if (response.status >= 200 && response.status < 300) {
        //console.log('API call successful:', response.config.method?.toUpperCase(), response.config.url?.substring(20));
      }
      return response;
    },
    // Error response - handle 401/403
    (error) => {
      // Log all errors for debugging
      if (error.response) {
        console.warn('API error:', error.response.status, error.config.method?.toUpperCase(), error.config.url?.substring(20));
      } else {
        console.warn('[API error (no response):', error.message);
      }

      // Handle location_disabled with force_logout flag (403 status)
      // When superadmin disables a location, users should be immediately kicked out
      if (error.response && error.response.status === 403) {
        const code = error.response.data?.code;
        const forceLogout = error.response.data?.force_logout;
        //const errorMessage = error.response.data?.error || 'Access denied';

        // Check if this is a location_disabled error with force_logout flag
        if (code === 'location_disabled' || forceLogout === true) {
          console.error('[SECURITY] Location disabled - forcing immediate logout');
          console.error('403 DETECTED - Location disable detected with code:', code);

          // For location disable, user sees no toast (clean silent logout on background API calls)
          // But if this comes through session/check poll, the frontend already handles it
          // Only show toast if it's from a user-initiated action
          if (!error.config.url?.includes('/session/check')) {
            toast.error('Your location has been disabled. Please contact your administrator.', {
              autoClose: 5000,
              hideProgressBar: false
            });
          }

          // Logout immediately with session_expired reason
          // No retry - force logout on first 403 with location_disabled
          if (performLogout && typeof performLogout === 'function') {
            performLogout('device', 'session_expired');
          } else {
            handleLogout();
          }

          // Reject the promise so calling code also sees the error
          return Promise.reject(error);
        }
      }

      if (error.response && error.response.status === 401) {
        // IMPORTANT: Distinguish between token revocation and validation failures
        // Validation failures (e.g., invalid_password) should NOT trigger logout
        // Token revocation (e.g., "logged out from another device") SHOULD trigger logout

        const reason = error.response.data?.reason;
        const errorMessage = error.response.data?.error || 'Request failed';

        // Check if this is a validation/authentication failure (not a token revocation)
        // These are localized errors that should be handled by the calling component
        if (reason === 'invalid_password') {
          console.warn('401 DETECTED - Validation failure (invalid_password):', errorMessage);
          // Reject the promise so calling code (e.g., ProfileSettings) can handle the error
          // NO TOAST MESSAGE - Let the component handle the error display
          return Promise.reject(error);
        }

        // Check if this is an actual token revocation
        // These are more serious and require logout
        const isTokenRevocation = reason &&
                                   (reason.includes('logged out') ||
                                    reason.includes('revoked') ||
                                    reason.includes('expired') ||
                                    (reason.includes('invalid') && reason.includes('token')));

        if (isTokenRevocation) {
          console.error('401 DETECTED - Token has been revoked!', reason);

          // Show toast notification only for actual token revocation
          toast.error(reason, {
            autoClose: 3000,
            hideProgressBar: false
          });

          // Logout the user - pass reason if performLogout is available
          if (performLogout && typeof performLogout === 'function') {
            performLogout('device', 'token_invalid');
          } else {
            handleLogout();
          }

          // Reject the promise so calling code also sees the error
          return Promise.reject(error);
        }

        // For other 401 errors without a clear reason, also treat as token revocation for safety
        console.error('401 DETECTED - Unknown token error:', errorMessage);
        toast.error(errorMessage || 'Your session has expired. Please login again.', {
          autoClose: 3000,
          hideProgressBar: false
        });
        if (performLogout && typeof performLogout === 'function') {
          performLogout('device', 'token_invalid');
        } else {
          handleLogout();
        }
        return Promise.reject(error);
      }

      // For non-401/403 errors, reject normally
      return Promise.reject(error);
    }
  );
};

export default axios;
